import type { TicketResource } from '@repo/api-client';

/**
 * Whether a ticket was voided.
 *
 * The nullable-timestamp annotation gap this was written around (`#209`) is
 * **closed** as of the 10 Aug 2026 regeneration: the field is a real
 * `string | null` and a screen may now say when. Nothing needs unwinding — the
 * predicate is still the right shape for the question it answers — but a screen
 * that wants the moment no longer has to pretend it cannot have it.
 *
 * One function rather than the check written out at each site, for the reason
 * `isRetired` is one function: the list and the record are two screens answering
 * the same question about the same field, and the day the annotation is fixed
 * there is one place that stops being a presence check.
 *
 * **A voided ticket is found and shown marked, never reported as missing**
 * (FR-333). Somebody disputing a charge that was voided needs to see that it was
 * voided — that is the answer to their question, and it is why nothing anywhere
 * uses this to filter a ticket out.
 */
export const isVoided = (ticket: TicketResource): boolean =>
  ticket.voidedAt !== null && ticket.voidedAt !== undefined;

/**
 * Whether to offer voiding this ticket — which is **not** the same question as
 * whether the API will accept one.
 *
 * The answer here is the half a screen can honestly know: a ticket that is
 * already voided has nothing left to void. Pressing it anyway is not an error —
 * the API answers `200` with the original outcome and writes nothing — so this
 * is about not offering an action with no effect, rather than about preventing
 * one.
 *
 * ## The half this does not answer, and why that is a choice
 *
 * The other cut-off is the **ticket's own business day**: a ticket from a day
 * that has since closed cannot be voided even while its outlet has a day open
 * right now, and the API refuses that with `409 TICKETING_DAY_CLOSED`. The
 * ticket said to decide the affordance from that day.
 *
 * **That state is readable, and this comment used to say it was not.** The old
 * wording — "there is no business-day route in the document at all, so there is
 * nothing to read the state from" — was true when it was written on 9 Aug and
 * has been false since `#127` regenerated the client. It is corrected here
 * rather than softened, because it read as a settled finding and the next person
 * to need this would have believed it instead of re-checking:
 *
 * - `GET /v1/business-days` — `BusinessDaysController_list_v1`, in the generated
 *   client, `@RequireRole('OWNER', 'MANAGER')` and **no** outlet scope. That is
 *   the same reach both ticket screens are guarded to, so this is not a route
 *   the caller would be refused.
 * - `BusinessDayResource` carries **`state`: `OPEN | CLOSED`** — "whether the day
 *   is open *now*", and explicitly not a one-way transition, because an owner
 *   may reopen a closed day. It also carries `businessDate`, `openedAt`,
 *   `closedAt`, `closedBy` and `reopenCount`.
 * - Two more routes read one day directly:
 *   `GET /v1/outlets/{outletId}/business-days/{businessDate}` and its
 *   `/reconciliation`.
 *
 * So a ticket's `businessDayId` **can** be resolved to an open-or-closed state.
 *
 * **It is still not read here, and the reason is cost against value rather than
 * impossibility.** The list does not filter by id, and `TicketResource` carries
 * no `businessDate` to address the by-date route with — only `businessDayId` —
 * so resolving one ticket's day means paging that outlet's days until the id
 * matches. On a *list* of tickets that is a page of them, spent deciding the
 * disabled state of a control the server adjudicates anyway.
 *
 * And the `409` is authoritative whatever the client believed a moment earlier.
 * A day can be closed — or reopened — between the fetch and the press, so the
 * pre-check does not remove the refusal path, it only adds a second way for the
 * screen to be wrong: a control greyed out against a day that has since reopened
 * is worse than one that asks and is told no. What the pre-check buys is a
 * slightly earlier "no" for somebody who was going to be told no anyway.
 *
 * **If this is revisited, the route is named above and the state is one field.**
 * The case that would justify it is a screen that already holds the day — a day
 * close or a reconciliation screen, where the fetch is not extra — rather than a
 * ticket list adding one for this.
 *
 * Two things follow, and both are deliberate:
 *
 * - **Nothing here guesses from a clock.** The obvious substitute — compare
 *   `occurredAt` against today — is wrong in exactly the case the rule exists
 *   for: the cut-off is a *day's* state and not an age, so a ticket from
 *   yesterday may be perfectly voidable and one from this morning may not.
 *   Guessing would hide a control that works and offer one that does not.
 * - **The `409` is handled where it lands.** A screen that offers this must word
 *   `TICKETING_DAY_CLOSED` when it arrives, and word it differently from
 *   already-voided: the copy for both is in `error-messages.ts`, keyed by code.
 *
 * ## Do not add a client-side already-voided guard to the *terminal* path
 *
 * The server decides already-voided **before** the closed-day check on purpose:
 * a till that voided a ticket at 21:55, lost the response, and replays after the
 * outlet closed is answered `200` rather than refused. That ordering is what
 * keeps an offline correction from stalling, and a client that pre-empted either
 * check would break exactly the case it was written for.
 */
export const isVoidable = (ticket: TicketResource): boolean => !isVoided(ticket);

/* -------------------------------------------------------------------------- */
/* Why it was voided                                                          */
/* -------------------------------------------------------------------------- */

/**
 * What a screen can say about *why* a ticket was voided.
 *
 * **Three states and not two, because `voidReason` alone cannot tell you which
 * of them you are in.** The field is `string | null` and its null covers
 * exactly one case — a void with no reason recorded — and never "this ticket
 * was not voided". `voidedAt` is the only field that answers that, which is why
 * this reads both and hands back a discriminated union rather than a nullable
 * string: a caller holding one of these cannot accidentally render a void as a
 * live ticket, because the two are different tags rather than the same `null`.
 *
 * That is the whole hazard the field's own published description names, and it
 * is worth restating here because both states are reachable **on purpose**:
 *
 * - **Voided at a desk** always carries a reason, because `VoidTicketDialog`
 *   insists on one. See its own note for why it exceeds `VoidTicketDto` there.
 * - **Voided at a till** may carry none, deliberately: there is a visitor
 *   waiting for every sentence typed at a gate, and a mis-key corrected ten
 *   seconds later nearly explains itself. Nobody failed to do anything.
 *
 * So a missing reason is an ordinary record and not a hole in one, and the copy
 * below says so in those terms. **It must never render as a blank**: a blank
 * beside a voided ticket reads as an answer — either that the reason was empty,
 * or that the screen is broken, or worst of all that the ticket still stands.
 *
 * Whitespace is read as no reason. The back office trims before it sends, so
 * this app cannot produce one; the terminal route is not this app's, and a
 * value that renders as nothing is the same problem as a blank whatever its
 * length.
 */
export type VoidReasonReading =
  | { readonly kind: 'not-voided' }
  | { readonly kind: 'given'; readonly reason: string }
  | { readonly kind: 'none-given' };

/** The reading itself. One function, so no screen invents a fourth answer. */
export function readVoidReason(ticket: TicketResource): VoidReasonReading {
  if (!isVoided(ticket)) return { kind: 'not-voided' };

  const reason = ticket.voidReason?.trim() ?? '';

  return reason === '' ? { kind: 'none-given' } : { kind: 'given', reason };
}

/**
 * What a voided ticket with no reason says, on the list and on the record
 * alike — one sentence, so the two screens never describe one ticket
 * differently.
 *
 * Worded as a fact about the record and **not as a fault**. "Not provided"
 * reads as a form somebody skipped; "missing" reads as data lost. Neither is
 * what happened: the till was never asked, and this is what that looks like.
 */
export const NO_VOID_REASON_GIVEN = 'No reason was given';

/**
 * The sentence that explains the one above, for the record screen — where
 * somebody is reading one ticket carefully with a visitor at the counter, and
 * "no reason" is a thing they may be about to chase.
 *
 * It does not say the void happened *at a till*, because nothing on
 * `TicketResource` says where a void came from. It says which screens ask,
 * which is true, checkable, and enough to stop the chase.
 */
export const NO_VOID_REASON_EXPLAINED =
  'A void made at a till is not asked for one — there is a visitor waiting. Only the back office insists.';
